// Shared SEO constants and JSON-LD (schema.org) builders.
// The business details come from the site's brand settings (src/brand), which
// both the React app and scripts/generate-seo.mjs pass in via configureSeo().
// No imports on purpose: generate-seo.mjs loads this file directly.

export let SITE_URL = "";
export let SITE_NAME = "";
export let DEFAULT_IMAGE = "";
export let BUSINESS = { name: "", phone: "", email: "", address: {}, sameAs: [] };

/* Point every SEO helper at a brand (see src/brand/defaultBrand.json for the shape). */
export function configureSeo(brand) {
  SITE_URL = String(brand.siteUrl || "").replace(/\/+$/, "");
  SITE_NAME = brand.name;
  DEFAULT_IMAGE = brand.ogImageUrl || `${SITE_URL}/og-image.jpg`;
  BUSINESS = {
    name: brand.name,
    phone: brand.phone,
    email: brand.email,
    logo: brand.logoUrl || `${SITE_URL}/favicon.svg`,
    address: { ...brand.address },
    sameAs: Object.values(brand.social || {}).filter(Boolean),
  };
}

/* Absolute URL for a route, always with a trailing slash. The host only serves the
   pre-tagged build/<route>/index.html files for "/route/" — "/route" falls through
   to the generic SPA index.html — so the slash form is the canonical one. */
export function pageUrl(path) {
  const clean = String(path || "/").split(/[?#]/)[0].replace(/\/+$/, "");
  return `${SITE_URL}${clean}/`;
}

export function pageTitle(title) {
  return title ? `${title} | ${SITE_NAME}` : SITE_NAME;
}

export function travelAgencySchema() {
  return {
    "@context": "https://schema.org",
    "@type": "TravelAgency",
    "@id": `${SITE_URL}/#organization`,
    name: BUSINESS.name,
    url: SITE_URL,
    logo: BUSINESS.logo,
    image: DEFAULT_IMAGE,
    telephone: BUSINESS.phone,
    email: BUSINESS.email,
    priceRange: "₹₹",
    address: { "@type": "PostalAddress", ...BUSINESS.address },
    openingHoursSpecification: [{
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
      opens: "09:00",
      closes: "19:00",
    }],
    sameAs: BUSINESS.sameAs,
  };
}

export function websiteSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${SITE_URL}/#website`,
    name: SITE_NAME,
    url: SITE_URL,
    publisher: { "@id": `${SITE_URL}/#organization` },
  };
}

export function breadcrumbSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: item.name,
      item: pageUrl(item.path),
    })),
  };
}

function offer(price, path) {
  if (!price) return undefined;
  return {
    "@type": "Offer",
    price: Number(price),
    priceCurrency: "INR",
    availability: "https://schema.org/InStock",
    url: pageUrl(path),
    seller: { "@id": `${SITE_URL}/#organization` },
  };
}

/* Packages are TouristTrip, not Product: Product makes Google validate them as
   shop items (shipping, return policy, ratings) — fields that don't apply to tours. */
export function packageSchema(pkg) {
  const path = `/packages/${pkg.id}`;
  return {
    "@context": "https://schema.org",
    "@type": "TouristTrip",
    name: pkg.name,
    description: `${pkg.duration ? `${pkg.duration} ` : ""}holiday package to ${pkg.city}, ${pkg.country}.`,
    image: pkg.image ? [pkg.image] : undefined,
    url: pageUrl(path),
    touristType: pkg.category === "domestic" ? "Domestic travellers" : "International travellers",
    provider: { "@id": `${SITE_URL}/#organization` },
    offers: offer(pkg.price, path),
  };
}

export function hotelSchema(hotel) {
  return {
    "@context": "https://schema.org",
    "@type": "Hotel",
    name: hotel.name,
    description: hotel.description || undefined,
    image: hotel.image || undefined,
    starRating: hotel.stars ? { "@type": "Rating", ratingValue: Number(hotel.stars) } : undefined,
    address: { "@type": "PostalAddress", addressLocality: hotel.city, addressCountry: hotel.country },
    amenityFeature: Array.isArray(hotel.amenities)
      ? hotel.amenities.map((a) => ({ "@type": "LocationFeatureSpecification", name: a, value: true }))
      : undefined,
    priceRange: hotel.price_per_night ? `From ₹${Number(hotel.price_per_night).toLocaleString("en-IN")} per night` : undefined,
  };
}

export function visaSchema(visa) {
  const path = `/visa/${visa.id}`;
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: `${visa.country} ${visa.visa_type || "Tourist"} Visa Assistance`,
    name: `${visa.country} Visa for Indians`,
    areaServed: { "@type": "Country", name: "India" },
    provider: { "@id": `${SITE_URL}/#organization` },
    offers: offer(visa.price, path),
  };
}

export function faqSchema(faqs) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map(({ q, a }) => ({
      "@type": "Question",
      name: q,
      acceptedAnswer: { "@type": "Answer", text: a },
    })),
  };
}
